import React, { useCallback, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { Dropdown, Layout, Menu, Modal } from "antd";
import {
  LogoutOutlined,
  SettingOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { inject, observer } from "mobx-react";
import { IAppStore } from "../../stores/Interfaces";
import DropdownToggleItems from "../DropdownToggleItems";
import { Column } from "../common/Table/Interfaces";
import UserStore from "../../stores/UserStore";
import Navigation from "./config/nav";
import { generateColorFromString } from "../../helpers/color";
import { HEADER } from "../../constants/translates";
import Form from "../forms/ChangePassword";
import Authorize from "../Authorize";
import { generateByKey } from "../../helpers/hash";
import { User } from "../../models/Users/User";

interface Props {
  store?: IAppStore;
}

const getAvatar = (user: User | null | undefined): React.ReactNode | null => {
  if (!user) {
    return null;
  }

  let color = "";

  if (user) {
    color = generateColorFromString(user.name);
  }

  return (
    <div className="header__profile">
      {user?.name}
      <span
        className="header__profile-avatar"
        style={{ backgroundColor: color }}
      />
    </div>
  );
};

const getColumns = (columns: Column[], store: UserStore): Column[] =>
  columns.filter((column) =>
    store.authorize(column.onlyOnPermission, column.onlyOnRole)
  );

const Index: React.FC<Props> = ({ store }) => {
  const user = store?.userStore.getCurrentUser();
  const settingsStore = store?.settingsStore;

  const logout = useCallback(() => {
    store?.userStore.logout();
  }, [store]);

  const [visibleChangePasswordModal, setVisibleChangePasswordModal] = useState(
    false
  );

  const showChangePasswordModal = useCallback(() => {
    setVisibleChangePasswordModal(true);
  }, []);

  const onFinishHandler = useCallback(() => {
    setVisibleChangePasswordModal(false);
  }, []);

  const menu = useMemo(
    () => (
      <Menu>
        <Menu.Item icon={<UnlockOutlined />} onClick={showChangePasswordModal}>
          {HEADER.changePassword}
        </Menu.Item>
        <Menu.Item icon={<LogoutOutlined />} onClick={logout}>
          {HEADER.logout}
        </Menu.Item>
      </Menu>
    ),
    [logout, showChangePasswordModal]
  );

  const changeColumnsHandler = useCallback(
    (keys: string[]) => {
      if (settingsStore) {
        settingsStore.toggleColumnByKey(keys);
      }
    },
    [settingsStore]
  );

  const saveSettings = useCallback((): void => {
    if (settingsStore) {
      settingsStore.saveDeclarantActiveSettings();
    }
  }, [settingsStore]);

  return (
    <div className="header-wrapper">
      <Layout.Header className="header small">
        <ul className="header__nav">
          {Navigation.map((item, index) => {
            const navItem = (
              <li key={item.key} className="header__nav-item">
                <NavLink
                  to={item.path}
                  className="header__nav-link"
                  activeClassName="header__nav-link--active"
                  exact
                >
                  {item.title}
                </NavLink>
              </li>
            );

            return (
              <Authorize
                permission={item.authorizeByPermission}
                role={item.authorizeByRole}
                key={generateByKey(index.toString())}
              >
                {navItem}
              </Authorize>
            );
          })}
        </ul>
        <ul className="header__nav">
          <li key="account-dropdown" className="header__nav-item">
            <Dropdown overlay={menu} placement="bottomRight">
              <button
                type="button"
                className="ant-dropdown-link header__dropdown-btn"
                onClick={(e) => e.preventDefault()}
              >
                {getAvatar(user)}
              </button>
            </Dropdown>
          </li>
          <li key="account-settings" className="header__nav-item">
            <DropdownToggleItems
              onChange={changeColumnsHandler}
              items={
                user && settingsStore?.columns && store?.userStore
                  ? getColumns(store.settingsStore.columns, store.userStore)
                  : []
              }
              saveChanges={saveSettings}
              className="settings-dropdown"
              overlayClassName="settings-menu"
              showToggleAllButton
            >
              <SettingOutlined />
            </DropdownToggleItems>
          </li>
        </ul>
      </Layout.Header>
      <Modal
        visible={visibleChangePasswordModal}
        onCancel={onFinishHandler}
        footer={null}
      >
        <Form onFinish={onFinishHandler} />
      </Modal>
    </div>
  );
};

export default inject("store")(observer(Index));
